import { BaseUrl } from "../config/api";
import { toHttps } from "../utils/imageUtils";

export const getAccessoriesByProduct = async (productId) => {
  const id = typeof productId === 'object' ? (productId?._id || productId?.id || productId) : productId;
  const res = await fetch(`${BaseUrl}/accessories/product/${String(id)}`);

  if (!res.ok) {
    throw new Error("Failed to fetch accessories");
  }

  const data = await res.json();

  return data.map((acc) => {
    const selling =
      Number(acc.sellingPrice ?? acc.price ?? acc.selling_price ?? 0) || 0;
    const actual =
      Number(acc.actualPrice ?? acc.mrp ?? acc.actual_price ?? selling) || selling;
    const mappedImages = Array.isArray(acc.images)
      ? acc.images.map(toHttps).filter(Boolean)
      : acc.image
        ? [toHttps(acc.image)].filter(Boolean)
        : [];

    return {
      id: acc._id || acc.id || `acc-${Math.random().toString(36).slice(2, 9)}`,
      _id: acc._id || acc.id,
      name: acc.accessoryName || acc.name || "Accessory",
      price: `$${selling.toFixed(2)} AUD`,
      images: mappedImages,
      isAccessory: true,
      linkedProductId: typeof acc.linkedProductId === 'object'
        ? (acc.linkedProductId?._id || acc.linkedProductId)
        : acc.linkedProductId,
      parentProductId: acc.productId,
      discount:
        actual > 0 && selling > 0
          ? Math.max(0, Math.round(((actual - selling) / actual) * 100))
          : 0,
    };
  });
};

export const getAllAccessories = async () => {
  const productsRes = await fetch(`${BaseUrl}/products/getallproducts`);
  if (!productsRes.ok) {
    throw new Error("Failed to fetch products for accessories");
  }

  const allProducts = await productsRes.json();
  if (!Array.isArray(allProducts)) return [];

  // 1. Get products that are in the "accessories" category
  const standaloneAccessories = allProducts
    .filter(
      (p) =>
        p &&
        !p.isDeleted &&
        (p.categoryName?.toLowerCase() === "accessories" ||
          p.categoryName?.toLowerCase() === "accessory")
    )
    .map((p) => {
      const selling = Number(p.sellingPrice ?? 0);
      const actual = Number(p.actualPrice ?? selling);
      return {
        id: p._id,
        _id: p._id,
        name: p.productName,
        price: `$${selling.toFixed(2)} AUD`,
        images: Array.isArray(p.images) ? p.images.map(toHttps) : [],
        isAccessory: true,
        isProduct: true,
        discount:
          actual > 0 && selling > 0
            ? Math.max(0, Math.round(((actual - selling) / actual) * 100))
            : 0,
      };
    });

  // 2. Get linked accessories for all products
  const ids = allProducts
    .filter((p) => p && !p.isDeleted && p._id)
    .map((p) => p._id);

  const batches = await Promise.all(
    ids.map(async (id) => {
      try {
        return await getAccessoriesByProduct(id);
      } catch (error) {
        return [];
      }
    })
  );

  const linkedAccessories = batches.flat();

  // 3. Merge and dedupe
  const combined = [...standaloneAccessories, ...linkedAccessories];
  const deduped = [];
  const seen = new Set();

  combined.forEach((item) => {
    const key = String(item._id || item.id || "");
    if (!key || seen.has(key)) return;
    seen.add(key);
    deduped.push(item);
  });

  return deduped;
};
